import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEmployees } from '../../../store/slices/authSlice';
import { fetchEmployeeById, updateEmployee, resetEmployeeState } from '../../../store/slices/employeeManagementSlice';
import './EditEmployee.scss';

const EditEmployee = () => {
  const dispatch = useDispatch();
  const { employees } = useSelector(state => state.auth);
  const { loading, error, success, currentEmployee } = useSelector(state => state.employeeManagement);
  
  const [selectedempID, setSelectedempID] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    personalEmail: '',
    empID: '',
    designation: '',
    organogram: '',
    dob: '',
    phone: '',
    whatsApp: '',
    address: '',
    country: '',
    location: '',
    leaveEntitlement: 21,
    leaveBalance: 0,
    leaveTaken: 0,
    emergencyContact: '',
    emergencyNumber: '',
    emergencyRelation: '',
    preferredLang: '',
    allLang: '',
    userType: 'user'
  });
  
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  // Fetch all employees when component mounts
  useEffect(() => {
    dispatch(fetchEmployees());
    
    // Reset success state when component unmounts
    return () => {
      dispatch(resetEmployeeState());
    };
  }, [dispatch]);
  
  // Handle success message
  useEffect(() => {
    if (success) {
      setShowSuccessMessage(true);
      
      // Hide success message after 3 seconds
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
        dispatch(resetEmployeeState());
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);
  
  // Update form data when currentEmployee changes
  useEffect(() => {
    if (currentEmployee) {
      const data = { ...currentEmployee };
      
      // Convert timestamps to date strings for input
      if (data.dob && typeof data.dob.toDate === 'function') {
        data.dob = data.dob.toDate().toISOString().split('T')[0];
      }
      
      // Set image preview if available
      if (data.img) {
        setImagePreview(data.img);
      } else {
        setImagePreview(null);
      }
      
      setFormData(data);
    }
  }, [currentEmployee]);
  
  const handleEmployeeSelect = (e) => {
    const empID = e.target.value;
    setSelectedempID(empID);
    
    if (empID) {
      dispatch(fetchEmployeeById(empID));
    } else {
      // Reset form if no employee selected
      setFormData({
        name: '',
        email: '',
        personalEmail: '',
        empID: '',
        designation: '',
        organogram: '',
        dob: '',
        phone: '',
        whatsApp: '',
        address: '',
        country: '',
        location: '',
        leaveEntitlement: 21,
        leaveBalance: 0,
        leaveTaken: 0,
        emergencyContact: '',
        emergencyNumber: '',
        emergencyRelation: '',
        preferredLang: '',
        allLang: '',
        userType: 'user'
      });
      setImagePreview(null);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: parseInt(value, 10) || 0 }));
  };
  
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const validateForm = () => {
    const errors = {};
    
    // Required fields
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.empID.trim()) errors.empID = 'Employee ID is required';
    if (!formData.designation.trim()) errors.designation = 'Designation is required';
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    
    // Phone validation (optional)
    if (formData.phone && !formData.phone.startsWith('+')) {
      errors.phone = 'Phone number should start with country code (+)';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      dispatch(updateEmployee({
        empID: selectedempID,
        employeeData: formData,
        profileImage
      }));
    }
  };
  
  return (
    <div className="edit-employee-container">
      <div className="page-header">
        <h1>Edit Employee Information</h1>
        <p>Update details and information for existing employees</p>
      </div>
      
      {showSuccessMessage && (
        <div className="success-message">
          Employee information updated successfully!
        </div>
      )}
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      <div className="employee-selection">
        <label htmlFor="employeeSelect">Select Employee to Edit:</label>
        <select 
          id="employeeSelect" 
          value={selectedempID}
          onChange={handleEmployeeSelect}
          className="employee-select"
        >
          <option value="">-- Select an Employee --</option>
          {employees && employees.map(employee => (
            <option key={employee.empID} value={employee.empID}>
              {employee.name} ({employee.empID || employee.email})
            </option>
          ))}
        </select>
      </div>
      
      {selectedempID && (
        <div className="employee-form-container">
          {loading && !currentEmployee ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading employee data...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="employee-form">
              <div className="form-columns">
                <div className="left-column">
                  <h3>Basic Information</h3>
                  
                  <div className="form-group">
                    <label htmlFor="name">Full Name <span className="required">*</span></label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={formErrors.name ? 'error' : ''}
                    />
                    {formErrors.name && <div className="error-text">{formErrors.name}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Work Email <span className="required">*</span></label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={formErrors.email ? 'error' : ''}
                      disabled // Email should not be editable
                    />
                    {formErrors.email && <div className="error-text">{formErrors.email}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="personalEmail">Personal Email</label>
                    <input
                      type="email"
                      id="personalEmail"
                      name="personalEmail"
                      value={formData.personalEmail || ''}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="empID">Employee ID <span className="required">*</span></label>
                    <input
                      type="text"
                      id="empID"
                      name="empID"
                      value={formData.empID}
                      onChange={handleChange}
                      className={formErrors.empID ? 'error' : ''}
                    />
                    {formErrors.empID && <div className="error-text">{formErrors.empID}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="designation">Designation <span className="required">*</span></label>
                    <input
                      type="text"
                      id="designation"
                      name="designation"
                      value={formData.designation}
                      onChange={handleChange}
                      className={formErrors.designation ? 'error' : ''}
                    />
                    {formErrors.designation && <div className="error-text">{formErrors.designation}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="organogram">Department</label>
                    <select
                      id="organogram"
                      name="organogram"
                      value={formData.organogram || 'technical'}
                      onChange={handleChange}
                    >
                      <option value="technical">Technical</option>
                      <option value="management">Management</option>
                      <option value="hr">HR</option>
                      <option value="marketing">Marketing</option>
                      <option value="finance">Finance</option>
                      <option value="operations">Operations</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="dob">Date of Birth</label>
                    <input
                      type="date"
                      id="dob"
                      name="dob"
                      value={formData.dob || ''}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="userType">User Type</label>
                    <select
                      id="userType"
                      name="userType"
                      value={formData.userType || 'user'}
                      onChange={handleChange}
                    >
                      <option value="user">Regular User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  
                  <h3>Profile Image</h3>
                  <div className="form-group image-upload">
                    <label htmlFor="profileImage">Profile Picture</label>
                    <input
                      type="file"
                      id="profileImage"
                      name="profileImage"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    {imagePreview && (
                      <div className="image-preview">
                        <img src={imagePreview} alt="Profile preview" />
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="right-column">
                  <h3>Contact Information</h3>
                  
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="text"
                      id="phone"
                      name="phone"
                      value={formData.phone || ''}
                      onChange={handleChange}
                      className={formErrors.phone ? 'error' : ''}
                    />
                    {formErrors.phone && <div className="error-text">{formErrors.phone}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="whatsApp">WhatsApp Number</label>
                    <input
                      type="text"
                      id="whatsApp"
                      name="whatsApp"
                      value={formData.whatsApp || ''}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="address">Address</label>
                    <textarea
                      id="address"
                      name="address"
                      value={formData.address || ''}
                      onChange={handleChange}
                      rows="3"
                    ></textarea>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="country">Country</label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={formData.country || ''}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="location">Location</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location || ''}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <h3>Emergency Contact</h3>
                  
                  <div className="form-group">
                    <label htmlFor="emergencyContact">Emergency Contact Name</label>
                    <input
                      type="text"
                      id="emergencyContact"
                      name="emergencyContact"
                      value={formData.emergencyContact || ''}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="emergencyNumber">Emergency Contact Number</label>
                    <input
                      type="text"
                      id="emergencyNumber"
                      name="emergencyNumber"
                      value={formData.emergencyNumber || ''}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="emergencyRelation">Relationship</label>
                    <input
                      type="text"
                      id="emergencyRelation"
                      name="emergencyRelation"
                      value={formData.emergencyRelation || ''}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <h3>Leave Information</h3>
                  
                  <div className="form-group">
                    <label htmlFor="leaveEntitlement">Annual Leave Entitlement</label>
                    <input
                      type="number"
                      id="leaveEntitlement"
                      name="leaveEntitlement"
                      value={formData.leaveEntitlement || 21}
                      onChange={handleNumberChange}
                      min="0"
                      max="100"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="leaveBalance">Current Leave Balance</label>
                    <input
                      type="number"
                      id="leaveBalance"
                      name="leaveBalance"
                      value={formData.leaveBalance || 0}
                      onChange={handleNumberChange}
                      min="0"
                      max="100"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="leaveTaken">Leave Days Taken</label>
                    <input
                      type="number"
                      id="leaveTaken"
                      name="leaveTaken"
                      value={formData.leaveTaken || 0}
                      onChange={handleNumberChange}
                      min="0"
                      max="100"
                    />
                  </div>
                  
                  <h3>Additional Information</h3>
                  
                  <div className="form-group">
                    <label htmlFor="preferredLang">Preferred Language</label>
                    <input
                      type="text"
                      id="preferredLang"
                      name="preferredLang"
                      value={formData.preferredLang || ''}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="allLang">Other Languages</label>
                    <input
                      type="text"
                      id="allLang"
                      name="allLang"
                      value={formData.allLang || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" onClick={() => dispatch(fetchEmployeeById(selectedempID))} className="reset-button" disabled={loading}>
                  Reset Changes
                </button>
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Employee Information'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default EditEmployee; 